import { useEffect, useRef, useState } from "react";
import clsx from "clsx";

let heritageCatalogMenuIdCounter = 0;

function useStableDomId(prefix: string): string {
  const idRef = useRef<string | null>(null);
  if (idRef.current == null) {
    heritageCatalogMenuIdCounter += 1;
    idRef.current = `${prefix}-${heritageCatalogMenuIdCounter}`;
  }
  return idRef.current;
}

export type HeritageCatalogSyncKind = "branch" | "purchase_history";

type Props = {
  disabled?: boolean;
  branchDownloadDisabled?: boolean;
  branchDownloadDisabledReason?: string;
  catalogSyncInProgress?: boolean;
  activeSyncKind?: HeritageCatalogSyncKind | null;
  isDownloadingBranch?: boolean;
  isDownloadingPurchaseHistory?: boolean;
  onDownloadBranch: () => void | Promise<void>;
  onDownloadPurchaseHistory: () => void | Promise<void>;
  buttonClassName?: string;
};

/**
 * Catalog download for a Heritage vendor.
 *
 * Purchase history is the primary action and runs straight from the button.
 * Heritage sells well beyond landscaping — a great deal of pool — so pulling
 * everything stocked at a branch brings in thousands of items the company will
 * never order. What they have actually bought over the last twelve months is the
 * set worth linking, so that is what the button does.
 *
 * The branch catalog stays available behind the caret, because it is what tags
 * items with a branch for the send-purchase-order path, but it now carries the
 * confirmation: it is the download that pulls in product lines the company does
 * not sell.
 */
const HeritageCatalogDownloadMenu = ({
  disabled = false,
  branchDownloadDisabled = false,
  branchDownloadDisabledReason,
  catalogSyncInProgress = false,
  activeSyncKind = null,
  isDownloadingBranch = false,
  isDownloadingPurchaseHistory = false,
  onDownloadBranch,
  onDownloadPurchaseHistory,
  buttonClassName = "px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white font-bold transition disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation min-h-[44px]",
}: Props) => {
  const menuId = useStableDomId("heritage-catalog-menu");
  const rootRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmBranchDownload, setConfirmBranchDownload] = useState(false);

  const isBusy =
    catalogSyncInProgress || isDownloadingBranch || isDownloadingPurchaseHistory;

  useEffect(() => {
    if (!menuOpen) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [menuOpen]);

  const buttonLabel = (() => {
    if (isDownloadingBranch || (catalogSyncInProgress && activeSyncKind === "branch")) {
      return "Downloading branch catalog…";
    }
    if (
      isDownloadingPurchaseHistory ||
      (catalogSyncInProgress && activeSyncKind === "purchase_history")
    ) {
      return "Downloading purchase history…";
    }
    if (catalogSyncInProgress) {
      return "Download in progress…";
    }
    return "Download Purchase History";
  })();

  const handlePurchaseHistorySelect = () => {
    setMenuOpen(false);
    setConfirmBranchDownload(false);
    void onDownloadPurchaseHistory();
  };

  const handleBranchSelect = () => {
    setMenuOpen(false);
    setConfirmBranchDownload(true);
  };

  const handleConfirmBranchDownload = () => {
    setConfirmBranchDownload(false);
    void onDownloadBranch();
  };

  return (
    <div ref={rootRef} className="flex flex-col items-end gap-2 w-full sm:w-auto shrink-0">
      <div className="relative inline-flex w-full sm:w-auto max-w-full">
        <div className="inline-flex w-full sm:w-auto min-w-[10.5rem] max-w-full rounded overflow-hidden shadow-sm">
          <button
            type="button"
            className={clsx(
              buttonClassName,
              "flex-1 min-w-0 rounded-none rounded-l border-r border-orange-400 text-left sm:text-center"
            )}
            disabled={disabled || isBusy}
            title={
              isBusy
                ? buttonLabel
                : "Download the items your company ordered from Heritage in the last 12 months."
            }
            onClick={handlePurchaseHistorySelect}
          >
            <span className="block truncate">{buttonLabel}</span>
          </button>
          <button
            type="button"
            className={clsx(
              buttonClassName,
              "flex-shrink-0 rounded-none rounded-r px-3 min-w-[44px]"
            )}
            disabled={disabled || isBusy}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-controls={menuId}
            aria-label="Other catalog download options"
            onClick={() => setMenuOpen((open) => !open)}
          >
            ▾
          </button>
        </div>

        {menuOpen && !isBusy && (
          <div
            id={menuId}
            role="menu"
            className="absolute right-0 top-full z-20 mt-1 min-w-[16rem] w-max max-w-[min(20rem,calc(100vw-2rem))] rounded-md border border-gray-200 bg-white py-1 shadow-lg"
          >
            <button
              type="button"
              role="menuitem"
              className="block w-full px-4 py-3 text-left text-sm text-gray-900 hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
              disabled={branchDownloadDisabled}
              title={branchDownloadDisabled ? branchDownloadDisabledReason : undefined}
              onClick={handleBranchSelect}
            >
              <span className="font-semibold block">Download Branch Items</span>
              <span className="text-xs text-gray-600 mt-0.5 block">
                Everything Heritage stocks at the selected branch, including product lines you do
                not sell.
              </span>
            </button>
          </div>
        )}
      </div>

      {confirmBranchDownload && !isBusy && (
        <div
          className="w-full sm:max-w-md rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950 space-y-3"
          role="dialog"
          aria-labelledby={`${menuId}-branch-confirm-title`}
        >
          <p id={`${menuId}-branch-confirm-title`} className="m-0 font-medium">
            Download the full branch catalog?
          </p>
          <p className="m-0 text-xs text-amber-900">
            This loads every item Heritage stocks at the selected branch, which covers product
            lines beyond landscaping. Most companies only need Download Purchase History — the
            items they have actually ordered in the last 12 months.
          </p>
          <div className="flex flex-wrap gap-2 justify-end">
            <button
              type="button"
              className="inline-flex min-h-[44px] items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-800 hover:bg-gray-50 touch-manipulation"
              onClick={() => setConfirmBranchDownload(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex min-h-[44px] items-center justify-center rounded-md bg-orange-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-700 touch-manipulation"
              onClick={handleConfirmBranchDownload}
            >
              Download branch catalog
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeritageCatalogDownloadMenu;
